import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useToast } from '../context/ToastContext';
import { getApiUrl } from '../config/api';
import { getStoredUser, PackageKey } from '../services/subscriptionService';
import cardStyles from '../styles/modules/Card.module.css';
import buttonStyles from '../styles/modules/Button.module.css';
import styles from '../styles/modules/BillingSettings.module.css';

interface PackageGrant {
    type: string;
    quantity: number;
    label: string;
}

interface AvailablePackage {
    key: PackageKey;
    label: string;
    grants: PackageGrant[];
    priceDisplay?: string;
    configured?: boolean;
    variantId?: string;
}

type TransferModalTab = 'details' | 'ips';

const normalizePackageKey = (value: unknown): PackageKey | null => {
    if (typeof value !== 'string') {
        return null;
    }

    const normalized = value.replace(/[_\s-]+/g, '').toLowerCase();

    if (normalized === 'jobpost' || normalized === 'jobrequest' || normalized === 'jobrequests') {
        return 'jobPost';
    }

    if (normalized === 'quicksearch') {
        return 'quickSearch';
    }

    if (normalized === 'small') {
        return 'small';
    }

    if (normalized === 'medium') {
        return 'medium';
    }

    if (normalized === 'large') {
        return 'large';
    }

    return null;
};

const inferGrantTypeFromPackage = (key: PackageKey, index: number): string => {
    if (key === 'jobPost') {
        return 'JobPost';
    }

    if (key === 'quickSearch') {
        return 'QuickSearch';
    }

    // Bundle fallback: first grant is JobPost, second is QuickSearch
    return index === 0 ? 'JobPost' : 'QuickSearch';
};

const normalizeGrantType = (value: unknown): string | null => {
    if (typeof value !== 'string') {
        return null;
    }

    const normalized = value.replace(/[_\s-]+/g, '').toLowerCase();

    if (normalized === 'jobpost' || normalized === 'jobrequest' || normalized === 'jobrequests') {
        return 'JobPost';
    }

    if (normalized === 'quicksearch') {
        return 'QuickSearch';
    }

    return value.trim() || null;
};

const toReadableLabel = (value: string): string => {
    if (!value) {
        return 'Token';
    }

    return value
        .replace(/[_-]+/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .trim();
};

const normalizeGrantQuantity = (grant: Record<string, unknown>): number => {
    const quantityCandidate = grant.quantity ?? grant.count ?? grant.amount ?? grant.credits ?? grant.qty;

    if (typeof quantityCandidate === 'number' && Number.isFinite(quantityCandidate)) {
        return quantityCandidate;
    }

    if (typeof quantityCandidate === 'string') {
        const parsed = Number(quantityCandidate);
        if (Number.isFinite(parsed)) {
            return parsed;
        }
    }

    return 1;
};

const formatPriceDisplay = (entry: Record<string, unknown>): string | undefined => {
    const formatted = entry.priceFormatted ?? entry.formattedPrice ?? entry.displayPrice;
    if (typeof formatted === 'string' && formatted.trim()) {
        return formatted.trim();
    }

    const currencyCandidate = entry.currency ?? entry.currencyCode;
    const currency = typeof currencyCandidate === 'string' && currencyCandidate.trim()
        ? currencyCandidate.trim().toUpperCase()
        : null;

    const numericCandidates = [
        entry.price,
        entry.amount,
        entry.priceAmount,
        entry.unitAmount,
        entry.amountCents,
        entry.amount_cents,
        entry.priceCents,
        entry.price_cents
    ];

    const rawNumeric = numericCandidates.find((value) => typeof value === 'number' && Number.isFinite(value));

    if (typeof rawNumeric !== 'number') {
        return undefined;
    }

    const centsBased =
        typeof entry.amountCents === 'number' ||
        typeof entry.amount_cents === 'number' ||
        typeof entry.priceCents === 'number' ||
        typeof entry.price_cents === 'number' ||
        typeof entry.unitAmount === 'number';

    const normalizedAmount = centsBased ? rawNumeric / 100 : rawNumeric;

    if (currency) {
        try {
            return new Intl.NumberFormat(undefined, {
                style: 'currency',
                currency
            }).format(normalizedAmount);
        } catch {
            return `${normalizedAmount.toFixed(2)} ${currency}`;
        }
    }

    return normalizedAmount.toFixed(2);
};

const extractAvailablePackagesCollection = (payload: unknown): Record<string, unknown>[] => {
    if (Array.isArray(payload)) {
        return payload.filter((entry): entry is Record<string, unknown> => Boolean(entry) && typeof entry === 'object');
    }

    if (!payload || typeof payload !== 'object') {
        return [];
    }

    const record = payload as Record<string, unknown>;
    const nestedCollections = [
        record.packages,
        record.availablePackages,
        record.data,
        record.results,
        record.items
    ];

    const nestedArray = nestedCollections.find(Array.isArray);
    if (Array.isArray(nestedArray)) {
        return nestedArray.filter((entry): entry is Record<string, unknown> => Boolean(entry) && typeof entry === 'object');
    }

    return Object.values(record)
        .filter((entry): entry is Record<string, unknown> => Boolean(entry) && typeof entry === 'object')
        .filter((entry) => {
            const keyCandidate = entry.key ?? entry.packageKey ?? entry.id ?? entry.code ?? entry.name;
            return Boolean(normalizePackageKey(keyCandidate));
        });
};

const normalizeAvailablePackages = (payload: unknown): AvailablePackage[] => {
    return extractAvailablePackagesCollection(payload)
        .filter((entry): entry is Record<string, unknown> => Boolean(entry) && typeof entry === 'object')
        .reduce<AvailablePackage[]>((packages, entry) => {
            const key = normalizePackageKey(entry.key ?? entry.packageKey ?? entry.id ?? entry.code ?? entry.name);
            if (!key) {
                return packages;
            }

            const grantsSource = Array.isArray(entry.grants)
                ? entry.grants
                : Array.isArray(entry.packageGrants)
                    ? entry.packageGrants
                    : Array.isArray(entry.included)
                        ? entry.included
                        : Array.isArray(entry.tokens)
                            ? entry.tokens
                            : [];

            packages.push({
                key,
                label: typeof entry.label === 'string' && entry.label.trim() ? entry.label : String(entry.key || entry.packageKey || key),
                grants: grantsSource
                    .filter((grant): grant is Record<string, unknown> => Boolean(grant) && typeof grant === 'object')
                    .map((grant, index) => {
                        const rawType = grant.type ?? grant.packageType ?? grant.grantType ?? grant.name ?? grant.key;
                        const grantType = normalizeGrantType(rawType) || inferGrantTypeFromPackage(key, index);
                        const quantity = normalizeGrantQuantity(grant);
                        const label = toReadableLabel(grantType);

                        if (!label) {
                            return null;
                        }

                        return {
                            type: grantType,
                            quantity,
                            label
                        };
                    })
                    .filter((grant): grant is PackageGrant => Boolean(grant)),
                configured: typeof entry.configured === 'boolean'
                    ? entry.configured
                    : typeof entry.isConfigured === 'boolean'
                        ? entry.isConfigured
                        : true,
                priceDisplay: formatPriceDisplay(entry),
                variantId: typeof entry.variantId === 'string'
                    ? entry.variantId
                    : typeof entry.variant_id === 'string'
                        ? entry.variant_id
                        : undefined
            });

            return packages;
        }, [])
        .filter((entry) => Boolean(entry.key));
};

const fetchAvailablePackages = async (): Promise<AvailablePackage[]> => {
    try {
        const response = await fetch(getApiUrl('subscriptions/available-packages'), {
            method: 'GET',
            headers: {
                'Cache-Control': 'no-cache',
                'Pragma': 'no-cache'
            }
        });

        if (!response.ok) {
            return [];
        }

        const payload = await response.json();
        return normalizeAvailablePackages(payload);
    } catch (error) {
        console.error('Error fetching available packages:', error);
        return [];
    }
};

const bankTransferDefaults = {
    recipient: process.env.REACT_APP_BANK_TRANSFER_RECIPIENT || '',
    recipientAddress: process.env.REACT_APP_BANK_TRANSFER_RECIPIENT_ADDRESS || '',
    payer: process.env.REACT_APP_BANK_TRANSFER_PAYER || '',
    bankName: process.env.REACT_APP_BANK_TRANSFER_BANK_NAME || '',
    accountNumber: process.env.REACT_APP_BANK_TRANSFER_ACCOUNT || '',
    paymentCode: process.env.REACT_APP_BANK_TRANSFER_PAYMENT_CODE || '',
    referenceNumber: process.env.REACT_APP_BANK_TRANSFER_REFERENCE || ''
};

const toIpsSafeValue = (value: string): string => value.replace(/[|\r\n]+/g, ' ').trim();
const toIpsAlphanumeric = (value: string): string => value.replace(/[^a-zA-Z0-9]/g, '');

const computeMod97 = (digits: string): number => {
    let remainder = 0;

    for (let index = 0; index < digits.length; index += 1) {
        const code = digits.charCodeAt(index) - 48;
        if (code < 0 || code > 9) {
            return NaN;
        }
        remainder = (remainder * 10 + code) % 97;
    }

    return remainder;
};

const normalizeSerbianAccountModel97 = (rawAccount: string): string => {
    const digits = rawAccount.replace(/\D/g, '');

    if (digits.length < 16) {
        return '';
    }

    const base16 = digits.slice(0, 16);
    const remainder = computeMod97(`${base16}00`);
    if (!Number.isFinite(remainder)) {
        return '';
    }

    const control = (98 - remainder) % 97;
    const controlPart = String(control).padStart(2, '0');
    const normalized = `${base16}${controlPart}`;

    // Model 97 validation for full 18-digit recipient account.
    return computeMod97(normalized) === 1 ? normalized : '';
};

const extractAmountForIps = (value: string): string => {
    const numeric = value.replace(/[^\d.,-]/g, '');
    if (!numeric) {
        return '';
    }

    const lastComma = numeric.lastIndexOf(',');
    const lastDot = numeric.lastIndexOf('.');
    const decimalIndex = Math.max(lastComma, lastDot);

    const integerPartRaw = decimalIndex >= 0 ? numeric.slice(0, decimalIndex) : numeric;
    const decimalPartRaw = decimalIndex >= 0 ? numeric.slice(decimalIndex + 1) : '';

    const integerPart = integerPartRaw.replace(/[^\d-]/g, '');
    const decimalPart = decimalPartRaw.replace(/\D/g, '').slice(0, 2);

    if (!integerPart || integerPart === '-') {
        return '';
    }

    const normalized = decimalPart ? `${integerPart}.${decimalPart}` : integerPart;
    const amount = Number(normalized);

    if (!Number.isFinite(amount)) {
        return '';
    }

    return amount.toFixed(2).replace('.', ',');
};

const BillingSettings: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { showToast } = useToast();
    const storedUser = useMemo(() => getStoredUser(), []);
    const [selectedPackage, setSelectedPackage] = useState<PackageKey | null>(null);
    const [availablePackages, setAvailablePackages] = useState<AvailablePackage[]>([]);
    const [isLoadingPrices, setIsLoadingPrices] = useState(true);
    const [showBankTransferModal, setShowBankTransferModal] = useState(false);
    const [activeTransferTab, setActiveTransferTab] = useState<TransferModalTab>('details');

    const selectedPackageDetails = useMemo(
        () => availablePackages.find((entry) => entry.key === selectedPackage) || null,
        [availablePackages, selectedPackage]
    );

    useEffect(() => {
        setIsLoadingPrices(true);
        fetchAvailablePackages()
            .then((packages) => {
                setAvailablePackages(packages);

                setSelectedPackage(packages[0]?.key || null);

                if (packages.length === 0) {
                    console.warn('No packages could be fetched from the subscriptions API.');
                }
            })
            .catch((error) => {
                console.error('Error loading plan prices:', error);
            })
            .finally(() => {
                setIsLoadingPrices(false);
            });
    }, []);

    const handleCreateCheckout = () => {
        const token = localStorage.getItem('authToken');
        if (!token) {
            showToast({ message: t('common.loginRequired') || 'Please log in first', type: 'error' });
            navigate('/signin');
            return;
        }

        if (!selectedPackage) {
            showToast({ message: t('subscription.selectPackageFirst') || 'Select a package first.', type: 'error' });
            return;
        }

        setActiveTransferTab('details');
        setShowBankTransferModal(true);
    };

    const packageDisplayName = selectedPackageDetails
        ? (t(`subscription.packages.${selectedPackageDetails.key}`) || selectedPackageDetails.label)
        : '-';

    const packagePrice = selectedPackageDetails?.priceDisplay || (t('subscription.priceAtCheckout') || 'Price at checkout');

    const ipsAmount = extractAmountForIps(packagePrice);
    const ipsAccount = normalizeSerbianAccountModel97(bankTransferDefaults.accountNumber);
    const isIpsAccountValid = Boolean(ipsAccount);
    const ipsPaymentCode = bankTransferDefaults.paymentCode.replace(/\D/g, '').slice(0, 3) || '289';
    const transferPurpose = t('subscription.transfer.purposeValue');
    const userBusinessName = (storedUser as { businessName?: string | null } | null)?.businessName || '';
    const ipsPayer = (userBusinessName || storedUser?.fullName || '').trim();

    const transferSummary = [
        `${t('subscription.transfer.package') || 'Package'}: ${packageDisplayName}`,
        `${t('subscription.transfer.amount') || 'Amount'}: ${packagePrice}`,
        `${t('subscription.transfer.recipient') || 'Recipient'}: ${bankTransferDefaults.recipient}`,
        ...(bankTransferDefaults.recipientAddress ? [`${t('subscription.transfer.address') || 'Address'}: ${bankTransferDefaults.recipientAddress}`] : []),
        `${t('subscription.transfer.bank') || 'Bank'}: ${bankTransferDefaults.bankName}`,
        `${t('subscription.transfer.accountNumber') || 'Account number'}: ${ipsAccount || bankTransferDefaults.accountNumber}`,
        `${t('subscription.transfer.paymentCode') || 'Payment code'}: ${ipsPaymentCode}`,
        `${t('subscription.transfer.purpose') || 'Purpose'}: ${transferPurpose}`
    ].join('\n');

    const ipsPayloadParts = isIpsAccountValid ? [
        'K:PR',
        'V:01',
        'C:1',
        `R:${toIpsSafeValue(ipsAccount)}`,
        `N:${toIpsSafeValue(bankTransferDefaults.recipient)}`,
        bankTransferDefaults.recipientAddress ? `RL:${toIpsAlphanumeric(bankTransferDefaults.recipientAddress)}` : '',
        ipsPayer ? `P:${toIpsSafeValue(ipsPayer)}` : '',
        ipsAmount ? `I:RSD${ipsAmount}` : '',
        `SF:${toIpsSafeValue(ipsPaymentCode)}`,
        `S:${toIpsSafeValue(transferPurpose)}`
    ].filter(Boolean) : [];

    const ipsPayload = ipsPayloadParts.join('|');
    const ipsQrImageUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&color=2c5f7f&data=${encodeURIComponent(ipsPayload)}`;

    const handleCopyTransferDetails = async () => {
        if (!navigator?.clipboard) {
            showToast({
                message: t('subscription.transfer.copyUnavailable') || 'Clipboard is not available in this browser.',
                type: 'error'
            });
            return;
        }

        try {
            await navigator.clipboard.writeText(transferSummary);
            showToast({
                message: t('subscription.transfer.copySuccess') || 'Transfer details copied.',
                type: 'success'
            });
        } catch {
            showToast({
                message: t('subscription.transfer.copyFailed') || 'Failed to copy transfer details.',
                type: 'error'
            });
        }
    };

    const handleCopyIpsPayload = async () => {
        if (!ipsPayload) {
            showToast({
                message: t('subscription.transfer.invalidAccountForIps') || 'Recipient account for IPS must contain exactly 18 digits.',
                type: 'error'
            });
            return;
        }

        if (!navigator?.clipboard) {
            showToast({
                message: t('subscription.transfer.copyUnavailable') || 'Clipboard is not available in this browser.',
                type: 'error'
            });
            return;
        }

        try {
            await navigator.clipboard.writeText(ipsPayload);
            showToast({
                message: t('subscription.transfer.copyIpsSuccess') || 'IPS payload copied.',
                type: 'success'
            });
        } catch {
            showToast({
                message: t('subscription.transfer.copyIpsFailed') || 'Failed to copy IPS payload.',
                type: 'error'
            });
        }
    };

    return (
        <div className={cardStyles.cardGlass}>
            <h3 className={styles.title}>{t('subscription.title') || 'Packages'}</h3>

            <div className={styles.ctaSection}>
                <label className={styles.label}>
                    {t('subscription.choosePackage') || 'Choose Package'}
                </label>

                {isLoadingPrices ? (
                    <div className={styles.loadingPrices}>
                        {t('common.loading') || 'Loading packages...'}
                    </div>
                ) : availablePackages.length === 0 ? (
                    <div className={styles.noPrices}>
                        <p>{t('subscription.packagesUnavailable') || 'Packages could not be loaded. Please try again later.'}</p>
                    </div>
                ) : (
                    <div className={styles.plansGrid}>
                        {availablePackages.map((entry) => (
                            <button
                                type="button"
                                key={entry.key}
                                className={`${styles.planCard} ${selectedPackage === entry.key ? styles.selected : ''}`}
                                onClick={() => {
                                    setSelectedPackage(entry.key);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        setSelectedPackage(entry.key);
                                    }
                                }}
                            >
                                <h4 className={styles.planName}>
                                    {t(`subscription.packages.${entry.key}`) || entry.label}
                                </h4>
                                <div className={styles.includedTitle}>
                                    {t('subscription.included') || 'Included'}
                                </div>
                                <div className={styles.includedList}>
                                    {entry.grants.map((grant) => (
                                        <span key={`${entry.key}-${grant.type}`} className={styles.period}>
                                            {grant.quantity} {t(`subscription.grants.${grant.type}`, { defaultValue: grant.label })}
                                        </span>
                                    ))}
                                </div>
                                <div className={styles.priceFooter}>
                                    <div className={styles.price}>
                                        {entry.priceDisplay || (t('subscription.priceAtCheckout') || 'Price at checkout')}
                                    </div>
                                </div>
                                <div className={styles.selectionIndicator}>
                                    {selectedPackage === entry.key && (
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M20 6L9 17l-5-5"/>
                                        </svg>
                                    )}
                                </div>
                            </button>
                        ))}
                    </div>
                )}

                <button
                    type="button"
                    className={buttonStyles.primary}
                    onClick={handleCreateCheckout}
                    disabled={!selectedPackageDetails}
                >
                    {t('subscription.purchaseCta') || 'Buy Package'}
                </button>
                <p className={styles.metaText}>
                    {t('subscription.hostedCheckoutInfo') || 'Checkout is handled by bank transfer.'}
                </p>
            </div>

            {showBankTransferModal && (
                <div className={styles.transferOverlay} onClick={() => setShowBankTransferModal(false)}>
                    <div className={styles.transferModal} onClick={(e) => e.stopPropagation()}>
                        <h4 className={styles.transferTitle}>
                            {t('subscription.transfer.title') || 'Bank transfer details'}
                        </h4>
                        <p className={styles.transferSubtitle}>
                            {t('subscription.transfer.subtitle') || 'Use these details to complete your payment.'}
                        </p>

                        <div className={styles.transferTabs}>
                            <button
                                type="button"
                                className={`${styles.transferTab} ${activeTransferTab === 'details' ? styles.transferTabActive : ''}`}
                                onClick={() => setActiveTransferTab('details')}
                            >
                                {t('subscription.transfer.tabDetails') || 'Transfer details'}
                            </button>
                            <button
                                type="button"
                                className={`${styles.transferTab} ${activeTransferTab === 'ips' ? styles.transferTabActive : ''}`}
                                onClick={() => setActiveTransferTab('ips')}
                            >
                                {t('subscription.transfer.tabIps') || 'IPS QR'}
                            </button>
                        </div>

                        {activeTransferTab === 'details' ? (
                            <>
                                <div className={styles.transferGrid}>
                                    <div className={styles.transferRow}>
                                        <span>{t('subscription.transfer.package') || 'Package'}</span>
                                        <strong>{packageDisplayName}</strong>
                                    </div>
                                    <div className={styles.transferRow}>
                                        <span>{t('subscription.transfer.amount') || 'Amount'}</span>
                                        <strong>{packagePrice}</strong>
                                    </div>
                                    <div className={styles.transferRow}>
                                        <span>{t('subscription.transfer.recipient') || 'Recipient'}</span>
                                        <strong>{bankTransferDefaults.recipient}</strong>
                                    </div>
                                    {bankTransferDefaults.recipientAddress && (
                                        <div className={styles.transferRow}>
                                            <span>{t('subscription.transfer.address') || 'Address'}</span>
                                            <strong>{bankTransferDefaults.recipientAddress}</strong>
                                        </div>
                                    )}
                                    <div className={styles.transferRow}>
                                        <span>{t('subscription.transfer.bank') || 'Bank'}</span>
                                        <strong>{bankTransferDefaults.bankName}</strong>
                                    </div>
                                    <div className={styles.transferRow}>
                                        <span>{t('subscription.transfer.accountNumber') || 'Account number'}</span>
                                        <strong>{ipsAccount || bankTransferDefaults.accountNumber}</strong>
                                    </div>
                                    <div className={styles.transferRow}>
                                        <span>{t('subscription.transfer.paymentCode') || 'Payment code'}</span>
                                        <strong>{ipsPaymentCode}</strong>
                                    </div>
                                    <div className={styles.transferRow}>
                                        <span>{t('subscription.transfer.purpose') || 'Purpose'}</span>
                                        <strong>{transferPurpose}</strong>
                                    </div>
                                </div>

                                <p className={styles.transferHint}>
                                    {t('subscription.transfer.ipsHint') || 'If your app supports IPS Show, switch to the IPS tab and scan the QR code.'}
                                </p>
                            </>
                        ) : (
                            <div className={styles.ipsTabContent}>
                                <p className={styles.transferHint}>
                                    {t('subscription.transfer.ipsScanHint') || 'Open your mobile banking app, choose IPS Show scan, then scan this code.'}
                                </p>

                                {ipsPayload ? (
                                    <div className={styles.ipsQrContainer}>
                                        <img
                                            src={ipsQrImageUrl}
                                            alt={t('subscription.transfer.ipsQrAlt') || 'IPS QR code'}
                                            className={styles.ipsQrImage}
                                        />
                                    </div>
                                ) : (
                                    <p className={styles.transferHint}>
                                        {t('subscription.transfer.invalidAccountForIps') || 'Recipient account for IPS must contain exactly 18 digits.'}
                                    </p>
                                )}

                                <label className={styles.ipsPayloadLabel}>
                                    {t('subscription.transfer.ipsPayloadLabel') || 'IPS payload'}
                                </label>
                                <pre className={styles.ipsPayload}>{ipsPayload}</pre>
                            </div>
                        )}

                        <div className={styles.transferActions}>
                            {activeTransferTab === 'details' ? (
                                <button type="button" className={`${buttonStyles.secondary} ${styles.transferSecondaryAction}`} onClick={handleCopyTransferDetails}>
                                    {t('subscription.transfer.copyButton') || 'Copy transfer details'}
                                </button>
                            ) : (
                                <button type="button" className={`${buttonStyles.secondary} ${styles.transferSecondaryAction}`} onClick={handleCopyIpsPayload}>
                                    {t('subscription.transfer.copyIpsButton') || 'Copy IPS payload'}
                                </button>
                            )}
                            <button type="button" className={buttonStyles.primary} onClick={() => setShowBankTransferModal(false)}>
                                {t('common.close') || 'Close'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BillingSettings;
